import { useGoogleLogin } from "@react-oauth/google";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { requestEndpoint } from "../../data/requestEndpoint";
import { encryptData } from "../../services/encryption.service";
import { NotificationService } from "../../services/Notification.service";
import { AuthV2Service } from "../../services/authV2Service";
import { AppleAuthService } from "../../services/appleAuth.service";
import { useAuthV2ReCaptcha } from "../../hooks/useAuthV2ReCaptcha";
import { post } from "../../services/useFetchAction";
import { useStorePageManager } from "../../store/PageManage";
import { useStoreUserManager } from "../../store/UserManage";
import { useStoreAuthV2 } from "../../store/AuthV2Store";
import "../../styles/components/connexionPanel.css";
// V1 panels (kept but hidden by default)
import { ConnectPanelConnexionPart } from "./ConnectPanelConnexionPart";
import { FinishPanelConnexionPart } from "./FinishPanelConnexionPart";
import { ForgetPasssPanelConnexion } from "./ForgetPasssPanelConnexion";
import { InscriptionPanelConnexionPart } from "./InscriptionPanelConnexionPart";
import { NewPassPanelConnexion } from "./NewPassPanelConnexion";
// V2 panels
import { AuthV2EntryPanel } from "./AuthV2EntryPanel";
import { AuthV2LoginPanel } from "./AuthV2LoginPanel";
import { AuthV2OtpPanel } from "./AuthV2OtpPanel";
import { AuthV2CompletePanel } from "./AuthV2CompletePanel";
import { AuthV2SocialPhonePanel } from "./AuthV2SocialPhonePanel";
import { AuthV2RecoveryPanel } from "./AuthV2RecoveryPanel";
import { AuthV2SecondaryContactPanel } from "./AuthV2SecondaryContactPanel";

export default function ConnexionPanel() {
  const { t } = useTranslation();
  const panelOpen = useStorePageManager((state) => state.connexionPanel);
  const handleUpdateConnexionPanelState = useStorePageManager(
    (state) => state.handleUpdateConnexionPanelState
  );
  const { executeSocialRegister } = useAuthV2ReCaptcha();
  const {
    setProspectId,
    setIdentifier,
    setFormData,
    setCurrentStep,
    resetAuthV2State,
    setSocialLoginData,
    setNeedsSecondaryContact,
    setNeedsContact,
  } = useStoreAuthV2();

  // V1 refs (kept for login v1)
  const refInputConnexionEmail = useRef(null);
  const refInputConnexionMdp = useRef(null);
  const refFirstName = useRef(null);
  const refLastName = useRef(null);
  const refPassword = useRef(null);

  const [panelState, setPanelState] = useState({
    // V2 panels
    authV2Entry: 1, // Visible by default
    authV2Login: 2,
    authV2Otp: 2,
    authV2Complete: 2,
    authV2SocialPhone: 2,
    authV2Recovery: 2,
    authV2SecondaryContact: 2,
    // V1 panels (hidden by default, accessible via redirect)
    inscriptionBegin: 2,
    inscriptionFinish: 2,
    connexion: 2,
    passwordForgotten: 2,
    newPassword: 2,
  });

  const [dataInscription, setDataInscription] = useState({
    addressMail: "",
    telephone: "",
    naming: "",
    firstName: "",
    mdp: "",
    indicatif: "",
  });

  const [token, setToken] = useState("");
  const [requestId, setRequestId] = useState("");

  // Reset all state when panel closes
  useEffect(() => {
    if (!panelOpen) {
      setPanelState({
        authV2Entry: 1,
        authV2Login: 2,
        authV2Otp: 2,
        authV2Complete: 2,
        authV2SocialPhone: 2,
        authV2Recovery: 2,
        authV2SecondaryContact: 2,
        inscriptionBegin: 2,
        inscriptionFinish: 2,
        connexion: 2,
        passwordForgotten: 2,
        newPassword: 2,
      });
      setDataInscription({
        addressMail: "",
        telephone: "",
        naming: "",
        firstName: "",
        birthday: "",
        mdp: "",
        indicatif: "",
      });
      resetAuthV2State();
    }
  }, [panelOpen]);

  // ─── OAuth V2 : Google ───────────────────────────────
  const handleSocialLoginSuccess = async (socialData) => {
    try {
      const captchaToken = await executeSocialRegister();

      const response = await AuthV2Service.socialRegister({
        ...socialData,
        captchaToken,
      });

      const result = response.data;

      // Cas 1 : Utilisateur existant → connecté directement
      if (result.access_token) {
        const dataToken = {
          access_token: result.access_token,
          refresh_token: result.refresh_token,
        };
        const encryptToken = await encryptData(dataToken);
        localStorage.setItem("token", encryptToken);

        const dataUserSave = {
          token: result.token || "",
          email: result.email,
        };
        const dataEncrypted = await encryptData(dataUserSave);
        localStorage.setItem("user", dataEncrypted);

        useStoreUserManager.getState().handleUpdateUser({
          id: result.id,
          lastname: result.lastname,
          firstname: result.firstname,
          location: result.location || "",
          connected: true,
          profil:
            result.profile + `${result.enableHost ? "/hote" : ""}`,
          mail: result.email,
          contact: result.contact || null,
          contactChecked: result.contactActivatedAt && true,
          emailVerified: result.mailActivatedAt,
          primaryIdentifierType: "EMAIL",
          pieceChecked: result.identityDoc ? 1 : 0,
          identityDoc: result.identityDoc ?? null,
          profilImgUrl: result.avatar ?? null,
          isSocial: true,
          typeSocial: socialData.socialType,
          enableHost: result.enableHost || false,
          payment_method_id: result.payment_method_id ?? 0,
        });

        // Vérifier si l'utilisateur a besoin d'ajouter un contact principal (téléphone)
        if (result.needsContact) {
          setSocialLoginData({
            socialEmail: socialData.socialEmail,
            socialType: socialData.socialType,
          });
          setNeedsContact(true);
          setIdentifier(socialData.socialEmail, "EMAIL");

          NotificationService.addToQueue(
            0,
            t("modals.connexionPanel.authV2.socialLoginSuccess")
          );

          setPanelState({
            ...panelState,
            authV2Entry: 2,
            authV2SecondaryContact: 1,
          });
          useStorePageManager.getState().handleUpdateBlockScreen(false);
          return;
        }

        // Vérifier si l'utilisateur a besoin d'ajouter un contact secondaire
        if (result.needsSecondaryContact) {
          setSocialLoginData({
            socialEmail: socialData.socialEmail,
            socialType: socialData.socialType,
          });
          setNeedsSecondaryContact(true);
          setIdentifier(socialData.socialEmail, "EMAIL");

          NotificationService.addToQueue(
            0,
            t("modals.connexionPanel.authV2.socialLoginSuccess")
          );

          setPanelState({
            ...panelState,
            authV2Entry: 2,
            authV2SecondaryContact: 1,
          });
          useStorePageManager.getState().handleUpdateBlockScreen(false);
          return;
        }

        NotificationService.addToQueue(
          0,
          t("modals.connexionPanel.authV2.socialLoginSuccess")
        );
        useStorePageManager.getState().handleUpdateBlockScreen(false);
        handleUpdateConnexionPanelState(false);
        return;
      }

      // Cas 2 : Nouveau prospect, besoin de téléphone
      if (result.needsPhone) {
        setProspectId(result.prospectId);
        setIdentifier(socialData.socialEmail, "EMAIL");
        setFormData({
          firstname: socialData.firstname || "",
          lastname: socialData.lastname || "",
        });

        setPanelState({
          ...panelState,
          authV2Entry: 2,
          authV2SocialPhone: 1,
        });
        useStorePageManager.getState().handleUpdateBlockScreen(false);
        return;
      }

      // Cas 3 : Nouveau prospect, téléphone fourni → OTP envoyé
      if (result.prospectId) {
        setProspectId(result.prospectId);
        setIdentifier(socialData.socialEmail, "EMAIL");
        setFormData({
          firstname: socialData.firstname || "",
          lastname: socialData.lastname || "",
        });
        setCurrentStep("otp");

        setPanelState({
          ...panelState,
          authV2Entry: 2,
          authV2Otp: 1,
        });
        useStorePageManager.getState().handleUpdateBlockScreen(false);
      }
    } catch (error) {
      useStorePageManager.getState().handleUpdateBlockScreen(false);
      NotificationService.addToQueue(
        2,
        error.message || t("modals.connexionPanel.authV2.genericError")
      );
    }
  };

  const launchGoogleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      const url = "https://www.googleapis.com/oauth2/v3/userinfo";
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${response.access_token}`,
          },
        });
        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();

        await handleSocialLoginSuccess({
          socialId: data.sub,
          socialEmail: data.email.toLowerCase(),
          socialType: "google",
          statusEmail: data.email_verified,
          firstname: data.given_name,
          lastname: data.family_name,
        });
      } catch (error) {
        console.log(error);
        useStorePageManager.getState().handleUpdateBlockScreen(false);
        NotificationService.addToQueue(
          2,
          t("modals.connexionPanel.notification.socials.googleLoginError")
        );
      }
    },
    onError: () => {
      useStorePageManager.getState().handleUpdateBlockScreen(false);
      NotificationService.addToQueue(
        2,
        t("modals.connexionPanel.notification.socials.googleLoginError")
      );
    },
  });

  const launchAppleLogin = async () => {
    useStorePageManager.getState().handleUpdateBlockScreen(true);

    try {
      const socialData = await AppleAuthService.signIn();
      await handleSocialLoginSuccess(socialData);
    } catch (error) {
      console.error("Apple login flow failed in ConnexionPanel", {
        code: error?.code || null,
        message: error?.message || String(error),
      });

      useStorePageManager.getState().handleUpdateBlockScreen(false);

      if (error?.code === "APPLE_CANCELLED") {
        return;
      }

      const appleErrorMessage =
        error?.code === "APPLE_NOT_CONFIGURED"
          ? t("modals.connexionPanel.authV2.appleNotConfigured")
          : error?.code === "APPLE_MISSING_EMAIL"
            ? t("modals.connexionPanel.authV2.appleEmailRequired")
            : t("modals.connexionPanel.notification.socials.appleLoginError");

      NotificationService.addToQueue(2, appleErrorMessage);
    }
  };

  const facebookLoginVoid = (response) => {
    handleSocialLoginSuccess({
      socialId: response.data.userID,
      socialEmail: response.data.email.toLowerCase(),
      socialType: "facebook",
      statusEmail: true,
      firstname: response.data.short_name,
      lastname: response.data.last_name,
    });
  };

  return (
    panelOpen && (
      <div className="connexion-panel">
        <div
          className="background-touch"
          onClick={() => {
            handleUpdateConnexionPanelState(false);
          }}
        ></div>

        {/* ─── V2 Panels ─── */}
        <AuthV2EntryPanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2Entry}
          launchAppleLogin={launchAppleLogin}
          launchGoogleLogin={launchGoogleLogin}
          facebookLoginVoid={facebookLoginVoid}
        />
        <AuthV2LoginPanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2Login}
        />
        <AuthV2OtpPanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2Otp}
          setRequestId={setRequestId}
          setToken={setToken}
        />
        <AuthV2CompletePanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2Complete}
        />
        <AuthV2SocialPhonePanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2SocialPhone}
        />
        <AuthV2RecoveryPanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2Recovery}
        />
        <AuthV2SecondaryContactPanel
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.authV2SecondaryContact}
        />

        {/* ─── V1 Panels (kept for login redirect & password recovery) ─── */}
        <ConnectPanelConnexionPart
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.connexion}
          dataInscription={dataInscription}
          setDataInscription={setDataInscription}
          launchAppleLogin={launchAppleLogin}
          launchGoogleLogin={launchGoogleLogin}
          facebookLoginVoid={facebookLoginVoid}
          refInputConnexionEmail={refInputConnexionEmail}
          refInputConnexionMdp={refInputConnexionMdp}
        />
        <InscriptionPanelConnexionPart
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.inscriptionBegin}
          dataInscription={dataInscription}
          setDataInscription={setDataInscription}
          launchAppleLogin={launchAppleLogin}
          launchGoogleLogin={launchGoogleLogin}
          facebookLoginVoid={facebookLoginVoid}
          refFirstName={refFirstName}
          refLastName={refLastName}
          refPassword={refPassword}
        />
        <FinishPanelConnexionPart
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.inscriptionFinish}
          dataInscription={dataInscription}
          setDataInscription={setDataInscription}
          refFirstName={refFirstName}
          refLastName={refLastName}
          refPassword={refPassword}
        />
        <ForgetPasssPanelConnexion
          setRequestId={setRequestId}
          setToken={setToken}
          requestId={requestId}
          token={token}
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.passwordForgotten}
          dataInscription={dataInscription}
          setDataInscription={setDataInscription}
        />
        <NewPassPanelConnexion
          requestId={requestId}
          token={token}
          setPanelState={setPanelState}
          panelState={panelState}
          currentState={panelState.newPassword}
          dataInscription={dataInscription}
          setDataInscription={setDataInscription}
        />
      </div>
    )
  );
}
